import { Config } from "./config";

interface Account { id: string; email: string; displayName: string }
interface AccountResponse { account?: Account | null; csrf?: string; error?: string; googleEnabled?: boolean; url?: string }
let csrf: string | undefined;
let account: Account | undefined;
let googleEnabled = false;
const base = (): string => Config.regions[Config.defaultRegion].mainAddress;

function showRegistration(enabled: boolean): void {
    const login = document.querySelector<HTMLElement>("#momo-account-login");
    const registration = document.querySelector<HTMLElement>("#momo-account-register");
    if (login) login.hidden = enabled;
    if (registration) registration.hidden = !enabled;
    const toggle = document.querySelector<HTMLButtonElement>("#momo-account-register-toggle");
    if (toggle) { toggle.textContent = enabled ? "Already registered? Sign in" : "New here? Create your account"; toggle.setAttribute("aria-expanded",String(enabled)); }
}

async function request(path: string, body?: Record<string,unknown>): Promise<AccountResponse> {
    const response = await fetch(`${base()}${path}`, {
        credentials: "include", cache: "no-store", signal: AbortSignal.timeout(10000),
        ...(body ? { method: "POST", headers: { "Content-Type":"application/json", "X-Momo-Client":"1", ...(csrf ? {"X-Momo-CSRF":csrf} : {}) },body:JSON.stringify(body) } : {})
    });
    const result = await response.json() as AccountResponse;
    if (!response.ok) throw new Error(result.error ?? "Unable to reach your account. Try again.");
    return result;
}
function update(result: AccountResponse): void {
    if(result.googleEnabled!==undefined)googleEnabled=result.googleEnabled;
    account = result.account ?? undefined;
    if (account) showRegistration(false);
    csrf = result.csrf;
    const trigger = document.querySelector<HTMLButtonElement>("#momo-account-button");
    if (trigger) trigger.textContent = account?.displayName ?? "Sign in";
    const callsign = document.querySelector<HTMLInputElement>("#username-input");
    if (callsign) {
        callsign.readOnly = true;
        callsign.value = account?.displayName ?? "Sign in to play";
        callsign.dispatchEvent(new Event("input",{bubbles:true}));
    }
    const signedIn = document.querySelector<HTMLElement>("#momo-account-signed-in");
    if (signedIn) signedIn.hidden = !account;
    document.querySelectorAll<HTMLElement>("[data-account-form]").forEach(element=>element.hidden=!!account);
    const summary = document.querySelector("#momo-account-summary");
    if (summary) summary.textContent = account ? `Signed in as ${account.displayName}` : "";
    for (const id of ["momo-account-google","momo-account-connect-google"]) {
        const button=document.querySelector<HTMLButtonElement>(`#${id}`);
        if(button){button.disabled=!googleEnabled;button.title=googleEnabled?"Sign in with your Google account":"Google sign-in is being set up. Email and password are available.";}
    }
    const note=document.querySelector<HTMLElement>("#momo-google-setup-note");
    if(note)note.hidden=googleEnabled;
    document.dispatchEvent(new CustomEvent("momo-account-change",{detail:account ?? null}));
}
export async function stagingAdmissionHeaders(): Promise<Headers | undefined> {
    try {
        update(await request("/api/account"));
        if (!account || !csrf) { document.querySelector<HTMLDialogElement>("#momo-account-dialog")?.showModal(); return; }
        return new Headers({"X-Momo-CSRF":csrf});
    } catch (error) {
        const status = document.querySelector("#momo-account-status");
        if (status) status.textContent = error instanceof Error ? error.message : "Unable to reach your account.";
        document.querySelector<HTMLDialogElement>("#momo-account-dialog")?.showModal();
    }
}
export function initAccounts(): void {
    if (Config.environment !== "staging") return;
    const dialog = document.querySelector<HTMLDialogElement>("#momo-account-dialog");
    const trigger = document.querySelector<HTMLButtonElement>("#momo-account-button");
    if (!dialog || !trigger) throw new Error("Account UI is missing");
    trigger.hidden = false;
    trigger.addEventListener("click",()=>{ void request("/api/account").then(update).catch(()=>update({account:null})); dialog.showModal(); });
    const status = document.querySelector<HTMLElement>("#momo-account-status")!;
    const google=():void=>{
        const referral=document.querySelector<HTMLInputElement>("#momo-account-invite")?.value ?? "";
        status.textContent="Opening Google…";
        void request("/api/account/google/start",{referral}).then(result=>{
            if(!result.url)throw new Error("Google sign-in is unavailable.");
            const target=new URL(result.url);
            if(target.origin!=="https://accounts.google.com")throw new Error("Google sign-in is unavailable.");
            location.assign(target.href);
        }).catch(error=>status.textContent=error instanceof Error ? error.message : "Please try again.");
    };
    document.querySelector("#momo-account-google")?.addEventListener("click",google);
    document.querySelector("#momo-account-connect-google")?.addEventListener("click",google);
    dialog.querySelectorAll<HTMLFormElement>("form").forEach(form=>form.addEventListener("submit", event=>{
        event.preventDefault();
        const fields: Record<string,string> = {};
        new FormData(form).forEach((value,key)=>{if(typeof value === "string")fields[key]=value;});
        const buttons = dialog.querySelectorAll<HTMLButtonElement>("button");
        buttons.forEach(button=>button.disabled=true);
        status.textContent = "Connecting…";
        void request(`/api/account/${form.dataset.accountAction}`,fields).then(result=>{
            update(result);
            status.textContent = "You’re signed in. Choose your entry and press Play Now.";
            form.reset(); dialog.close();
        }).catch(error=>status.textContent=error instanceof Error ? error.message : "Please try again.")
            .finally(()=>buttons.forEach(button=>button.disabled=false));
    }));
    document.querySelector("#momo-account-logout")?.addEventListener("click",()=>{
        void request("/api/account/logout",{}).then(result=>{update(result);status.textContent="Signed out.";}).catch(error=>status.textContent=error instanceof Error ? error.message : "Please try again.");
    });
    document.querySelector("#momo-account-register-toggle")?.addEventListener("click",()=>{
        const form = document.querySelector<HTMLElement>("#momo-account-register");
        if (form) showRegistration(form.hidden);
    });
    document.querySelectorAll<HTMLElement>(".momo-admin-choice").forEach(element=>element.hidden=true);
    const tag = document.querySelector(".momo-test-tag");
    if (tag) tag.textContent = "PRIVATE TEST";
    const fragment=new URLSearchParams(location.hash.slice(1));
    const invite=fragment.get("invite"),referral=new URLSearchParams(location.search).get("ref"),googleResult=fragment.get("google");
    if (invite || referral) {
        const field = document.querySelector<HTMLInputElement>("#momo-account-invite");
        if (field && referral && /^[A-Za-z0-9]{6}$/.test(referral)) field.value = referral.toUpperCase();
        showRegistration(true);
        history.replaceState(null,"",`${location.pathname}${location.search}`);
        dialog.showModal();
    }
    void request("/api/account").then(result=>{
        update(result);
        if ((invite || referral) && !account) showRegistration(true);
        if(googleResult){
            history.replaceState(null,"",`${location.pathname}${location.search}`);
            if(googleResult!=="success"){
                status.textContent=googleResult==="link-required" ? "This email already has an account. Sign in with your password, then connect Google from your account." : "Google sign-in was not completed. Try again or use email and password.";
                dialog.showModal();
            }
        }
    }).catch(()=>status.textContent="Account server unavailable. Please try again.");
}
